import {AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {combineLatest, Subject, Subscription} from 'rxjs';
import {NavigationEnd, Router} from '@angular/router';
import {filter, takeUntil} from 'rxjs/operators';
import {UserApiService} from '@app/services/api/user-api.service';
import {SwitchToFullModeDialogComponent} from '@app/dialogs/switch-to-full-mode-dialog/switch-to-full-mode-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ScrollToTopService} from '@app/services/scroll-to-top.service';
import {InAppNotification} from '@app/interfaces/entities/in-app-notification';
import {NotificationApiService} from '@app/services/api/notification-api.service';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {SidebarStateService} from "@app/services/util/sidebar-state.service";
import {AuthService} from '@app/services/auth.service';

@Component({
  selector: 'app-admin-root',
  templateUrl: './admin-root.component.html',
  styleUrls: ['./admin-root.component.scss'],
  standalone: false
})
export class AdminRootComponent implements OnInit, OnDestroy, AfterViewInit {
  isNotInAdministration = true;
  private destroy$ = new Subject<void>();
  private subscription: Subscription = new Subscription();
  private scrollSubscription: Subscription;
  private userSubscription: Subscription = new Subscription();
  public isDemo;
  public loudNotifications$: Subject<InAppNotification[]>;
  public AccountFeaturesEnum = AccountFeaturesEnum;

  /**
   * URL patterns for pages that have been migrated to Design 2.0 and therefore
   * have both a v2 and a legacy variant. The version-toggle banner is only
   * visible on these routes so it doesn't appear on pages that haven't been
   * redesigned yet (where there's no "legacy" alternative to toggle to).
   *
   * Grow this list as more pages get migrated. Keep it anchored (`^/admin/...`)
   * and terminate each pattern with a segment/query/end boundary so partial
   * matches don't leak through.
   */
  public readonly abMigratedRoutes: RegExp[] = [
    /^\/admin\/vehicles\/active(-legacy)?(\/|\?|$)/,
    /^\/admin\/vehicles\/archived(-legacy)?(\/|\?|$)/,
    /^\/admin\/vehicles\/imported(-legacy)?(\/|\?|$)/,
    // Vehicle detail page — path ends in a UUID, toggle via localStorage + reload.
    /^\/admin\/vehicles\/[0-9a-f]{8}-[0-9a-f]{4}/i,
    // Vehicle add/edit form (Design 2.0) — both `form` and `form/:uuid`.
    /^\/admin\/vehicles\/form(-legacy)?(\/|\?|$)/,
    // Vehicle "Add from VIN" entry page (Design 2.0).
    /^\/admin\/vehicles\/create(-legacy)?(\/|\?|$)/,
    // Unified advertising list (Design 2.0). No trailing `/` — `ads/...` are
    // the per-vehicle ad form pages, which are not migrated.
    /^\/admin\/vehicles\/ads(-legacy)?(\?|$)/,
  ];

  @ViewChild('pageWindow') pageWindow;

  // The navbar is `position: fixed`, so its rendered height isn't reserved by
  // normal document flow - the content below must offset itself by that
  // height manually. It used to be a hardcoded 55px, but the navbar's trial
  // banner / account-name pills wrap onto extra lines at ~768-1279px widths,
  // growing the real height well past 55px and sliding the page content
  // underneath the fixed navbar. Track the actual height instead.
  public navHeight = 55;
  private navResizeObserver?: ResizeObserver;

  constructor(
    private router: Router,
    private notificationApiService: NotificationApiService,
    private userApiService: UserApiService,
    private matDialog: MatDialog,
    private scrollToTopService: ScrollToTopService,
    protected sidebarState: SidebarStateService,
    private authService: AuthService,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
  ) {
    //  Make sure that you have this in constructor, otherwise you don't get the first page load event
    this.subscription.add(
      this.router
        .events.pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntil(this.destroy$)
      )
        .subscribe((event: NavigationEnd) => {
          this.checkUrl(event.urlAfterRedirects);
          this.sidebarState.setShrunk(true)
        }));

    this.subscription.add(scrollToTopService.getObserable().subscribe(() => this.scrollToTop()));
  }

  ngOnInit() {
    this.userSubscription.add(this.userApiService.getIsDemoStream().subscribe((result) => {
      this.isDemo = result;
    }));

    // Only fetch notifications if the user is logged in
    if (this.authService.isLoggedIn()) {
      // TODO: This must be made with Websockets and also with one request
      this.notificationApiService.startFetchingNotifications();
    }

    this.loudNotifications$ = this.notificationApiService.loudNotifications;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.subscription.unsubscribe();
    this.scrollSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
    this.navResizeObserver?.disconnect();
  }

  private checkUrl(url: string) {
    this.isNotInAdministration = url.indexOf('/admin/administration') === -1;
  }

  ngAfterViewInit(): void {
    // Skip scroll-to-top on vehicle detail pages — tab switches there should
    // preserve scroll position.
    this.scrollSubscription = this.router.events.pipe(
      filter(item => item instanceof NavigationEnd),
      filter(() => {
        const nav = this.router.getCurrentNavigation();
        if (!nav?.extractedUrl?.root?.children?.primary?.children?.tab) {
          return false;
        }
        return !/^\/admin\/vehicles\/[^/]+\//.test(this.router.url);
      }))
      .subscribe(() => {
        this.pageWindow.nativeElement.scrollTop = 0;
      });

    const nav = this.elementRef.nativeElement.querySelector('nav');
    if (nav) {
      this.navResizeObserver = new ResizeObserver((entries) => {
        const height = entries[0]?.contentRect.height;
        if (height) {
          this.ngZone.run(() => {
            this.navHeight = Math.round(height);
          });
        }
      });
      this.navResizeObserver.observe(nav);
    }
  }

  openSwitchToFullMode() {
    const dialogRef = SwitchToFullModeDialogComponent.open(this.matDialog);
  }

  private scrollToTop() {
    this.pageWindow.nativeElement.scrollTop = 0;
  }

}
